import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  FolderOpen, 
  CheckCircle2, 
  Phone,
  ArrowLeft,
  ArrowRight,
  FileText,
  MapPin,
  ClipboardList,
  Shield,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  FileText,
  MapPin,
  ClipboardList,
  Shield,
  AlertCircle,
  RefreshCw
};

export default function FieldFile() {
  const { t, i18n } = useTranslation('services-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const contents = [
    {
      icon: "MapPin",
      title: isRTL ? "מיפוי סיכונים" : "Risk Mapping",
      description: isRTL ? "מיפוי מלא של כל הסיכונים באתר העבודה" : "Complete mapping of all risks at the work site"
    },
    {
      icon: "ClipboardList",
      title: isRTL ? "נהלי חירום" : "Emergency Procedures",
      description: isRTL ? "תכנית חירום מותאמת לאתר ולפעילות" : "Emergency plan tailored to the site and activity"
    },
    {
      icon: "FileText",
      title: isRTL ? "הוראות עבודה בטוחה" : "Safe Work Instructions",
      description: isRTL ? "הנחיות מפורטות לביצוע עבודות בטוחות" : "Detailed guidelines for performing safe work"
    },
    {
      icon: "Shield",
      title: isRTL ? "ציוד מגן אישי" : "Personal Protective Equipment",
      description: isRTL ? "רשימת ציוד מגן נדרש ודרישות שימוש" : "Required protective equipment list and usage requirements"
    },
    {
      icon: "AlertCircle",
      title: isRTL ? "אזורים מסוכנים" : "Hazardous Areas",
      description: isRTL ? "סימון וזיהוי אזורים בעלי סיכון מוגבר" : "Marking and identification of high-risk areas"
    },
    {
      icon: "RefreshCw",
      title: isRTL ? "עדכונים שוטפים" : "Regular Updates",
      description: isRTL ? "עדכון התיק בהתאם לשינויים בשטח" : "Updating the file according to field changes"
    }
  ];

  const benefits = [
    isRTL ? "עמידה בדרישות החוק והתקנות" : "Compliance with laws and regulations",
    isRTL ? "מניעת תאונות עבודה" : "Prevention of work accidents",
    isRTL ? "מענה מהיר במקרי חירום" : "Quick response in emergencies",
    isRTL ? "תיעוד מסודר לביקורות" : "Organized documentation for inspections",
    isRTL ? "הגברת המודעות בקרב העובדים" : "Increased awareness among workers",
    isRTL ? "הפחתת חשיפה משפטית" : "Reduced legal exposure"
  ];

  return (
    <Layout>
      <Helmet>
        <title>{isRTL ? "תיק שטח | אדם בטיחות" : "Field File | Adam Safety"}</title>
        <meta name="description" content={isRTL ? "הכנת תיק שטח מקצועי הכולל מיפוי סיכונים, נהלי חירום והוראות עבודה בטוחה." : "Professional field file preparation including risk mapping, emergency procedures, and safe work instructions."} />
        <link rel="canonical" href="https://adam-safety.com/services/field-file" />
        <meta property="og:title" content={isRTL ? "תיק שטח | אדם בטיחות" : "Field File | Adam Safety"} />
        <meta property="og:description" content={isRTL ? "הכנת תיק שטח מקצועי הכולל מיפוי סיכונים, נהלי חירום והוראות עבודה בטוחה." : "Professional field file preparation including risk mapping, emergency procedures, and safe work instructions."} />
        <meta property="og:url" content="https://adam-safety.com/services/field-file" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={isRTL ? "תיק שטח | אדם בטיחות" : "Field File | Adam Safety"} />
        <meta name="twitter:description" content={isRTL ? "הכנת תיק שטח מקצועי הכולל מיפוי סיכונים, נהלי חירום והוראות עבודה בטוחה." : "Professional field file preparation including risk mapping, emergency procedures, and safe work instructions."} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <FolderOpen className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isRTL ? "תיעוד בטיחות" : "Safety Documentation"}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {isRTL ? "תיק שטח" : "Field File"}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {isRTL 
                ? "תיק שטח מקצועי ומקיף - המפתח לניהול בטיחות יעיל באתר העבודה שלכם. כל המידע החיוני במקום אחד."
                : "A professional and comprehensive field file - the key to effective safety management at your work site. All essential information in one place."
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {isRTL ? "להזמנת תיק שטח" : "Order a Field File"}
                </Button>
              </Link>
              <Link to="/services/safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {isRTL ? "שירותי בטיחות נוספים" : "More Safety Services"}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isRTL ? "מה כולל תיק השטח?" : "What's Included in the Field File?"}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isRTL 
                ? "תיק שטח מקצועי מותאם אישית לאתר העבודה שלכם"
                : "A professional field file customized for your work site"
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {contents.map((item, index) => {
              const IconComponent = iconMap[item.icon] || FileText;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-service"
                >
                  <div className="icon-container mb-4">
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isRTL ? "היתרונות של תיק שטח מקצועי" : "Benefits of a Professional Field File"}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 bg-card p-4 rounded-xl border border-border"
              >
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                <span className="text-foreground font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {isRTL ? "מוכנים להזמין תיק שטח?" : "Ready to Order a Field File?"}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {isRTL 
                ? "צרו קשר עוד היום ונכין עבורכם תיק שטח מקצועי ומותאם"
                : "Contact us today and we'll prepare a professional, customized field file for you"
              }
            </p>
            <Link to="/contact">
              <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                <Phone className="w-5 h-5" />
                {isRTL ? "צרו קשר" : "Contact Us"}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
